import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnDestroy,
  Output
} from "@angular/core";
import {CdkDragEnd} from "@angular/cdk/drag-drop";
import {
  DEFAULT_PANEL_DATA,
  MIN_PANEL_SIZE,
  HEADER_HEIGHT_OFFSET
} from "../../constants/commons";

export interface Vector {
  x: number;
  y: number;
}

export interface FloatingPanelData {
  position: Vector;
  size: Vector;
}

require('./floating-panel.scss');
@Component({
  standalone: false,
  selector: 'app-floating-panel',
  templateUrl: './floating-panel.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FloatingPanelComponent implements OnDestroy {
  @Input() title: string;
  @Input() isMinimized: boolean = false;
  @Input() zIndex: number = 75;
  @Input() grabHandleMargin: string = '0 150px 0 240px';

  @Input()
  set floatingPanelData(value: FloatingPanelData) {
    const data = value || DEFAULT_PANEL_DATA;
    const boundedPos = this.getBoundedPosition(data.position, data.size);
    const boundedSize = this.getBoundedSize(data.size, boundedPos);
    this.panelData = {
      position: boundedPos,
      size: boundedSize
    };
  }

  @Output() floatingPanelDataChange = new EventEmitter<FloatingPanelData>();
  @Output() isMinimizedChange = new EventEmitter<boolean>();
  @Output() onClose = new EventEmitter<void>();

  public panelData: FloatingPanelData = {...DEFAULT_PANEL_DATA};

  private isResizing = false;
  private resizeStartMousePosition: Vector;
  private resizeStartSize: Vector;

  private mouseMoveListener = (event: MouseEvent) => this.onDocumentMouseMove(event);
  private mouseUpListener = () => this.onDocumentMouseUp();

  constructor(@Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef) {}

  ngOnDestroy() {
    document.removeEventListener("mousemove", this.mouseMoveListener);
    document.removeEventListener("mouseup", this.mouseUpListener);
  }

  public onDragEnded(event: CdkDragEnd): void {
    const freeDragPosition = event.source.getFreeDragPosition();

    this.panelData = {
      ...this.panelData,
      position: this.getBoundedPosition(freeDragPosition, this.panelData.size)
    };

    this.emitPanelDataChange();
  }

  public toggleMinimize(event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isMinimized = !this.isMinimized;
    this.isMinimizedChange.emit(this.isMinimized);
  }

  public startResize(event: MouseEvent): void {
    if (this.isMinimized) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    this.isResizing = true;
    this.resizeStartMousePosition = {
      x: event.clientX,
      y: event.clientY
    };
    this.resizeStartSize = {...this.panelData.size};

    document.addEventListener("mousemove", this.mouseMoveListener);
    document.addEventListener("mouseup", this.mouseUpListener);
  }

  public onDocumentMouseMove(event: MouseEvent): void {
    if (!this.isResizing) {
      return;
    }

    const nextSize = {
      x: this.resizeStartSize.x + event.clientX - this.resizeStartMousePosition.x,
      y: this.resizeStartSize.y + event.clientY - this.resizeStartMousePosition.y
    };

    this.panelData = {
      ...this.panelData,
      size: this.getBoundedSize(nextSize, this.panelData.position)
    };

    this.cdr.markForCheck();
  }

  public onDocumentMouseUp(): void {
    if (!this.isResizing) {
      return;
    }

    this.isResizing = false;
    this.emitPanelDataChange();

    document.removeEventListener("mousemove", this.mouseMoveListener);
    document.removeEventListener("mouseup", this.mouseUpListener);
  }

  @HostListener("window:resize")
  public onWindowResize(): void {
    const boundedPos = this.getBoundedPosition(this.panelData.position, this.panelData.size);
    this.panelData = {
      position: boundedPos,
      size: this.getBoundedSize(this.panelData.size, boundedPos)
    };
  }

  private getBoundedPosition(position: Vector, size: Vector): Vector {
    const panelWidth = size?.x || MIN_PANEL_SIZE.x;
    const minX = 0;
    const maxX = window.innerWidth - panelWidth;
    const minY = 0;
    const maxY = window.innerHeight - HEADER_HEIGHT_OFFSET;

    return {
      x: Math.max(minX, Math.min(position.x, maxX)),
      y: Math.max(minY, Math.min(position.y, maxY))
    };
  }

  private getBoundedSize(size: Vector, position: Vector): Vector {
    return {
      x: Math.max(MIN_PANEL_SIZE.x, Math.min(size.x, window.innerWidth - position.x)),
      y: Math.max(MIN_PANEL_SIZE.y, Math.min(size.y, window.innerHeight - position.y))
    };
  }

  private emitPanelDataChange(): void {
    this.floatingPanelDataChange.emit({
      position: {...this.panelData.position},
      size: {...this.panelData.size}
    });
  }
}
